import { ref, provide, inject, InjectionKey, Ref } from 'vue'
import { useRouter } from 'vue-router'
import { logger, createLogger } from '@/utils/logger'

const popupLogger = createLogger('LoginPopup')

// Define the injection key and context interface
interface LoginPopupContext {
  showLoginPopup: (options?: { context?: LoginPopupReason }) => void
  hideLoginPopup: () => void
  isLoginPopupVisible: Ref<boolean>
  loginPopupContext: Ref<LoginPopupReason>
  cancelRedirect: () => void
}

const LoginPopupKey = Symbol('LoginPopup') as InjectionKey<LoginPopupContext>

// Create global state for the popup visibility
const isLoginPopupVisible = ref(false)

export type LoginPopupReason = 'default' | 'subscribe' | 'header'

const loginPopupContext = ref<LoginPopupReason>('default')

/**
 * Composable for managing the login popup
 * This is a simplified version that only handles showing/hiding the popup
 * Redirects are handled by the router
 */
export function useLoginPopup() {
  // Initialize router at the composable level
  const router = useRouter()

  /**
   * Show the login popup
   * @param options - Optional context for the modal (e.g. 'subscribe' shows message for subscribe/purchase)
   */
  const showLoginPopup = (options?: { context?: LoginPopupReason }) => {
    popupLogger.log('Showing login popup', options?.context)
    loginPopupContext.value = options?.context ?? 'default'
    isLoginPopupVisible.value = true
  }
  
  /**
   * Hide the login popup
   */
  const hideLoginPopup = () => {
    isLoginPopupVisible.value = false
    loginPopupContext.value = 'default'
  }
  
  /**
   * Cancel any pending redirect
   * Use this when a user chooses to continue as guest or when a login fails
   */
  const cancelRedirect = () => {
    popupLogger.log('Canceling pending redirect')
    
    // Remove both redirect paths to ensure no unwanted redirects
    localStorage.removeItem('redirectAfterLogin')
    localStorage.removeItem('tempRedirectAfterLogin')
  }
  
  const resolvePostLoginPath = (
    context: LoginPopupReason,
    redirectPath: string | null,
  ): string => {
    if (context === 'header' || !redirectPath || redirectPath.startsWith('/library')) {
      return '/'
    }

    if (redirectPath === '/login') {
      return '/'
    }

    return redirectPath
  }

  /**
   * Handle successful login
   */
  const handleLoginSuccess = () => {
    const context = loginPopupContext.value

    hideLoginPopup()

    // Check for temp redirect path first
    const tempRedirectPath = localStorage.getItem('tempRedirectAfterLogin')
    if (tempRedirectPath) {
      popupLogger.log('Restoring redirect path from temp storage:', tempRedirectPath)
      localStorage.setItem('redirectAfterLogin', tempRedirectPath)
      localStorage.removeItem('tempRedirectAfterLogin')
    }

    const redirectPath = localStorage.getItem('redirectAfterLogin')
    localStorage.removeItem('redirectAfterLogin')
    localStorage.removeItem('tempRedirectAfterLogin')

    const destination = resolvePostLoginPath(context, redirectPath)
    popupLogger.log('Redirecting to:', destination)
    router.push(destination)
  }

  return {
    isLoginPopupVisible,
    loginPopupContext,
    showLoginPopup,
    hideLoginPopup,
    handleLoginSuccess,
    cancelRedirect
  }
}

/**
 * Provide login popup functionality to child components
 */
export function provideLoginPopup() {
  const { showLoginPopup, hideLoginPopup, cancelRedirect } = useLoginPopup()
  
  provide(LoginPopupKey, {
    showLoginPopup,
    hideLoginPopup,
    isLoginPopupVisible,
    loginPopupContext,
    cancelRedirect
  })
}

/**
 * Inject login popup functionality in components
 * Only use this in components that absolutely need direct access to the login popup
 */
export function injectLoginPopup() {
  const context = inject(LoginPopupKey)
  
  if (!context) {
    popupLogger.warn('LoginPopup context not provided, using fallback')
    return {
      showLoginPopup: () => popupLogger.warn('LoginPopup not available'),
      hideLoginPopup: () => {},
      isLoginPopupVisible: ref(false),
      loginPopupContext: ref('default'),
      cancelRedirect: () => {}
    }
  }
  
  return context
} 